import React, { useState, useEffect, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createScopedLogger } from '../utils/logger.js';
import ValetService, { fetchVehicle } from './ValetService';

/**
 * Valet - Smart component loader for Lots (`odv-truest/src/lots`)
 * 
 * The complete metaphor:
 * - Garages: Feature domains (operations, finance, etc.)
 * - Lots: Specific features (assignments, clients, etc.)
 * - Vehicles: Components that run in the lots
 * - Valet: This service that fetches and manages everything
 */

// Loading states for better UX
const LoadingVehicle = ({ vehicleName, lotId }) => (
  <div className="flex items-center justify-center p-8">
    <div className="text-center">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vegas-gold mx-auto mb-4"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          🚗
        </div>
      </div>
      <p className="text-gray-400">Valet fetching {vehicleName}</p>
      <p className="text-xs text-gray-500 mt-1">from {lotId} lot</p>
    </div>
  </div>
);

// Error state with retry
const VehicleError = ({ error, retry, vehicleInfo }) => (
  <div className="p-6 bg-red-900/20 border border-red-800 rounded-lg">
    <div className="flex items-start space-x-3">
      <span className="text-2xl">🚫</span>
      <div className="flex-1">
        <h3 className="text-red-400 font-semibold mb-2">
          Vehicle Not Available
        </h3>
        <p className="text-red-300 text-sm">
          Unable to fetch "{vehicleInfo.vehicleName}" from "{vehicleInfo.lotId}" lot
        </p>
        <details className="mt-2">
          <summary className="cursor-pointer text-red-400 text-sm hover:text-red-300">
            Error Details
          </summary>
          <pre className="mt-2 text-xs bg-red-900/30 p-2 rounded overflow-auto text-red-200">
            {error.message}
          </pre>
        </details>
        <button 
          onClick={retry}
          className="mt-4 px-4 py-2 bg-red-800 text-white rounded hover:bg-red-700 transition-colors"
        >
          Call Valet Again
        </button>
      </div>
    </div>
  </div>
);

const log = createScopedLogger('Valet');

// eslint-disable-next-line no-unused-vars
const Valet = ({ lotId, brickId, garage: _garage, vehicleName = 'index', fallback, showStatus: _showStatus = false, ...vehicleProps }) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [Vehicle, setVehicle] = useState(null);

  // Support both new lotId and legacy brickId
  const actualLotId = lotId || brickId;

  log.debug?.('Received props', {
    lotId,
    brickId,
    vehicleName,
    actualLotId,
    expectedKey: `${actualLotId}.${vehicleName}`
  });

  useEffect(() => {
    let mounted = true;

    function loadVehicle() {
      setStatus('loading');
      setError(null);

      try {
        log.debug?.('Attempting to fetch vehicle', { parkingSpot: `${actualLotId}.${vehicleName}` });
        // Use fetchVehicle to get the vehicle (returns lazy component)
        const vehicle = fetchVehicle(actualLotId, vehicleName);
        log.debug?.('fetchVehicle result', { found: !!vehicle });

        if (!vehicle && fallback) {
          log.warn?.('Vehicle not found, using fallback', { vehicleName, lot: actualLotId });
          setVehicle(() => fallback);
        } else if (!vehicle) {
          throw new Error(`Vehicle "${vehicleName}" not found in "${actualLotId}" lot`);
        } else {
          log.debug?.('Vehicle resolved', { parkingSpot: `${actualLotId}.${vehicleName}` });
          if (mounted) {
            // Vehicle is already a lazy component, wrap in a function for React state
            setVehicle(() => vehicle);
          }
        }

        if (mounted) {
          setStatus('loaded');
        }
      } catch (err) {
        log.error?.('Failed to fetch vehicle', err);
        if (mounted) {
          setError(err);
          setStatus('error');
        }
      }
    }

    loadVehicle();

    return () => {
      mounted = false;
    };
  }, [actualLotId, vehicleName, fallback]);

  const retry = () => {
    setStatus('idle');
    setError(null);
    // Re-trigger useEffect by changing a dependency
  };

  // Loading state
  if (status === 'loading') {
    return <LoadingVehicle vehicleName={vehicleName} lotId={actualLotId} />;
  }

  // Error state
  if (status === 'error') {
    return (
        <VehicleError
          error={error}
          retry={retry}
          vehicleInfo={{ vehicleName, lotId: actualLotId }}
        />
    );
  }

  // Loaded state
  if (status === 'loaded' && Vehicle) {
    return (
      <ErrorBoundary
        FallbackComponent={({ error, resetErrorBoundary }) => (
          <VehicleError
            error={error}
            retry={resetErrorBoundary}
            vehicleInfo={{ vehicleName, lotId: actualLotId }}
          />
        )}
      >
        <Suspense fallback={<LoadingVehicle vehicleName={vehicleName} lotId={actualLotId} />}>
          <Vehicle {...vehicleProps} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Idle state
  return null;
};

export default Valet;
